/** Thrown by withDeadline() when the wrapped promise outlives its budget. */
export class DeadlineExceededError extends Error {
  constructor(label: string, ms: number) {
    super(`${label} timed out after ${ms}ms`);
    this.name = "DeadlineExceededError";
  }
}

/**
 * Rejects with DeadlineExceededError if `promise` hasn't settled within `ms`;
 * otherwise mirrors it. The underlying work is NOT cancelled — callers must
 * be safe against it settling later.
 *
 * Exists because Amplify's own network calls (and the axios instance) carry
 * no timeout, and on the iPad a socket left over from before a lock/wake can
 * stall with no response and no error — a spinner that never ends.
 */
export function withDeadline<T>(
  promise: Promise<T>,
  ms: number,
  label = "operation",
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new DeadlineExceededError(label, ms)),
      ms,
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}
